
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/use-toast';
import { Search, Save, Edit3, CheckSquare, Square, Upload, Download } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';
import { Procurement } from '@/api/entities';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const ENTITIES = [
  { value: 'PersonFinal', label: 'People' },
  { value: 'AccountFinal', label: 'Accounts' },
  { value: 'Procurement', label: 'Procurement' },
];

const PERSON_FIELDS = [
  { key: 'country', label: 'Country', type: 'select', options: ['Australia', 'Canada', 'Hong Kong', 'South Africa'] },
  { key: 'department', label: 'Department', type: 'select', options: ['Finance', 'Executive', 'Marketing', 'Student Affairs', 'IT', 'Teaching & Learning', 'Operations', 'Procurement'] },
  { key: 'seniority', label: 'Seniority', type: 'select', options: ['Director', 'VP', 'C-Suite', 'Manager', 'Coordinator', 'Analyst'] },
  { key: 'archetype_name', label: 'Archetype', type: 'text' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'worked_for_customer', label: 'Worked for Customer', type: 'boolean' },
  { key: 'add_to_pursuit', label: 'Add to Pursuit', type: 'boolean' },
  { key: 'persona_score_rationale', label: 'Persona Score Rationale', type: 'textarea' },
  { key: 'persona_fit_score', label: 'Persona Fit Score', type: 'number', min: 0, max: 100 },
];

const ACCOUNT_FIELDS = [
  { key: 'country', label: 'Country', type: 'select', options: ['Australia', 'Canada'] },
  { key: 'sector', label: 'Sector', type: 'select', options: ['Public University', 'Private University', 'Community College', 'Technical Institute'] },
  { key: 'procurement_friendliness', label: 'Procurement Friendliness', type: 'select', options: ['Friendly', 'Standard', 'Bureaucratic', 'Monitor'] },
  { key: 'pursuit_status', label: 'Pursuit Status', type: 'select', options: ['active_pursuit', 'no_pursuit'] },
  { key: 'tier', label: 'Tier', type: 'select', options: ['1', '2', '3', '4'] },
  { key: 'is_visible', label: 'Is Visible', type: 'boolean' },
  { key: 'incumbent_sentiment', label: 'Incumbent Sentiment', type: 'text' },
  { key: 'incumbent_sentiment_rationale', label: 'Incumbent Sentiment Rationale', type: 'textarea' },
  { key: 'crm_vendor', label: 'CRM Product', type: 'text' },
  { key: 'eportfolio_vendor', label: 'ePortfolio Product', type: 'text' },
  { key: 'proctoring_vendor', label: 'Proctoring Product', type: 'text' },
  { key: 'library_product', label: 'Library Product', type: 'text' },
  { key: 'admissions_product', label: 'Admissions Product', type: 'text' },
  { key: 'home_product', label: 'Home Product', type: 'text' },
];

const PROCUREMENT_FIELDS = [
  { key: 'status', label: 'Status', type: 'select', options: ['Live', 'Closed', 'Awarded', 'Cancelled'] },
  { key: 'country', label: 'Country', type: 'text' },
  { key: 'deadline', label: 'Deadline', type: 'date' },
  { key: 'published_date', label: 'Published Date', type: 'date' },
  { key: 'is_visible', label: 'Is Visible', type: 'boolean' },
];

export default function BatchFieldUpdater() {
  const [activeTab, setActiveTab] = useState('manual');
  const [selectedEntity, setSelectedEntity] = useState('PersonFinal');
  const [selectedField, setSelectedField] = useState('');
  const [fieldValue, setFieldValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [emptyFieldFilter, setEmptyFieldFilter] = useState(false);
  const [records, setRecords] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [isUploadingCsv, setIsUploadingCsv] = useState(false);
  
  const { toast } = useToast();

  const fieldOptions = selectedEntity === 'PersonFinal'
    ? PERSON_FIELDS 
    : selectedEntity === 'AccountFinal' 
    ? ACCOUNT_FIELDS 
    : PROCUREMENT_FIELDS;
    
  const selectedFieldConfig = fieldOptions.find(f => f.key === selectedField);

  const loadRecords = async () => {
    setIsLoading(true);
    setSelectedRecords(new Set());
    try {
      let data;
      if (selectedEntity === 'PersonFinal') {
        data = await PersonFinal.list();
      } else if (selectedEntity === 'AccountFinal') {
        data = await AccountFinal.list();
      } else if (selectedEntity === 'Procurement') {
        data = await Procurement.list();
      }
      setRecords(data);
      toast({ title: 'Records loaded', description: `Found ${data.length} records` });
    } catch (error) {
      console.error('Error loading records:', error);
      toast({ title: 'Error', description: 'Failed to load records', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRecord = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const toggleAll = () => {
    if (selectedRecords.size === filteredRecords.length) {
      setSelectedRecords(new Set());
    } else {
      setSelectedRecords(new Set(filteredRecords.map(r => r.id)));
    }
  };

  const handleBatchUpdate = async () => {
    if (selectedRecords.size === 0) {
      toast({ title: 'No records selected', variant: 'destructive' });
      return;
    }

    if (!selectedField || (fieldValue === '' && selectedFieldConfig?.type !== 'boolean')) {
      toast({ title: 'Please select a field and value', variant: 'destructive' });
      return;
    }

    setIsUpdating(true);

    try {
      const Entity = selectedEntity === 'PersonFinal'
        ? PersonFinal 
        : selectedEntity === 'AccountFinal' 
        ? AccountFinal 
        : Procurement;
      
      // Convert value based on field type
      let finalValue = fieldValue;
      if (selectedFieldConfig?.type === 'boolean') {
        finalValue = fieldValue === 'true';
      } else if (selectedFieldConfig?.type === 'number') {
        finalValue = parseFloat(fieldValue);
      } // For 'date' and 'text', fieldValue is already a string

      const updateData = { [selectedField]: finalValue };
      let successCount = 0;
      let errorCount = 0;

      toast({
        title: 'Processing...',
        description: `Updating ${selectedRecords.size} records. Please wait...`,
        duration: 10000
      });

      // Update each selected record
      for (const recordId of selectedRecords) {
        try {
          await Entity.update(recordId, updateData);
          successCount++;
          
          // Add delay to avoid rate limiting
          if (successCount < selectedRecords.size) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          // Progress update every 10 records
          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${selectedRecords.size} records`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Failed to update record ${recordId}:`, error);
          errorCount++;
        }
      }

      // Reload records to show updates
      await loadRecords();
      setSelectedRecords(new Set());
      setFieldValue('');

      if (errorCount === 0) {
        toast({
          title: 'Success!',
          description: `Updated ${successCount} records`
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${errorCount} failed`,
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Batch update error:', error);
      toast({
        title: 'Update failed',
        description: error.message || 'An error occurred',
        variant: 'destructive'
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      toast({
        title: 'Invalid file',
        description: 'Please select a CSV file',
        variant: 'destructive'
      });
    }
  };

  const downloadCsvTemplate = () => {
    const csv = `person_id,persona_score_rationale\nexample_id_123,"This is an example rationale that can be up to 200 characters long. You can write detailed explanations here about why this persona is a good fit."`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'persona_rationale_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const handleCsvUpload = async () => {
    if (!csvFile) {
      toast({
        title: 'No file selected',
        description: 'Please select a CSV file to upload',
        variant: 'destructive'
      });
      return;
    }

    setIsUploadingCsv(true);

    try {
      // Parse CSV
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      
      console.log('📄 CSV headers:', headers);
      
      // Validate headers
      if (!headers.includes('person_id') || !headers.includes('persona_score_rationale')) {
        throw new Error('CSV must have "person_id" and "persona_score_rationale" columns');
      }

      const personIdIndex = headers.indexOf('person_id');
      const rationaleIndex = headers.indexOf('persona_score_rationale');

      // Get all people records
      const allPeople = await PersonFinal.list();
      
      // Create lookup by person_id
      const peopleById = new Map();
      allPeople.forEach(p => {
        if (p.person_id) peopleById.set(p.person_id, p);
        // Also map by record id as fallback
        peopleById.set(p.id, p);
      });

      let successCount = 0;
      let errorCount = 0;
      let notFoundCount = 0;

      toast({
        title: 'Processing CSV...',
        description: `Updating ${lines.length - 1} records with persona_score_rationale`,
        duration: 10000
      });

      // Process each row (skip header)
      for (let i = 1; i < lines.length; i++) {
        try {
          // Handle quoted CSV values properly
          const row = lines[i].match(/(".*?"|[^,]+)(?=\s*,|\s*$)/g) || [];
          const values = row.map(v => v.trim().replace(/^"|"$/g, ''));
          
          const personId = values[personIdIndex];
          let rationale = values[rationaleIndex] || '';
          
          // Clean up rationale: remove extra quotes and trim
          rationale = rationale.replace(/^["']+|["']+$/g, '').trim();
          
          if (!personId || !rationale) {
            console.warn(`Row ${i}: Missing person_id or rationale`);
            errorCount++;
            continue;
          }

          // Find person by person_id or fallback to record id
          const person = peopleById.get(personId);
          
          if (!person) {
            console.warn(`Row ${i}: Person not found with person_id: ${personId}`);
            notFoundCount++;
            continue;
          }

          // Update the person with the new rationale
          await PersonFinal.update(person.id, { 
            persona_score_rationale: rationale 
          });
          
          successCount++;

          // Add delay to avoid rate limiting
          if (successCount < lines.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 700));
          }

          // Progress update every 10 records
          if (successCount % 10 === 0) {
            toast({
              title: 'Progress...',
              description: `Updated ${successCount} of ${lines.length - 1} records`,
              duration: 3000
            });
          }
        } catch (error) {
          console.error(`Error processing row ${i}:`, error);
          errorCount++;
        }
      }

      setCsvFile(null);
      
      if (errorCount === 0 && notFoundCount === 0) {
        toast({
          title: 'Success!',
          description: `Updated ${successCount} persona rationales`,
          duration: 5000
        });
      } else {
        toast({
          title: 'Partially completed',
          description: `${successCount} succeeded, ${notFoundCount} not found, ${errorCount} failed`,
          variant: 'default',
          duration: 7000
        });
      }
    } catch (error) {
      console.error('CSV upload error:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'An error occurred during upload',
        variant: 'destructive'
      });
    } finally {
      setIsUploadingCsv(false);
    }
  };

  const filteredRecords = records.filter(record => {
    const searchLower = searchTerm.toLowerCase();
    
    // Text search
    let matchesSearch = true;
    if (searchTerm) {
      if (selectedEntity === 'PersonFinal') {
        matchesSearch = `${record.first_name} ${record.last_name}`.toLowerCase().includes(searchLower) ||
               record.account_name?.toLowerCase().includes(searchLower);
      } else if (selectedEntity === 'AccountFinal') {
        matchesSearch = record.account_name?.toLowerCase().includes(searchLower);
      } else if (selectedEntity === 'Procurement') {
        matchesSearch = record.title?.toLowerCase().includes(searchLower) ||
               record.institution?.toLowerCase().includes(searchLower);
      }
    }
    
    // Status filter for Procurement
    let matchesStatus = true;
    if (selectedEntity === 'Procurement' && statusFilter !== 'all') {
      if (statusFilter === 'empty') {
        matchesStatus = !record.status || record.status === '';
      } else {
        matchesStatus = record.status === statusFilter;
      }
    }
    
    // Empty field filter
    let matchesEmptyField = true;
    if (emptyFieldFilter && selectedField) {
      const fieldVal = record[selectedField];
      matchesEmptyField = fieldVal === null || fieldVal === undefined || fieldVal === '';
    }
    
    return matchesSearch && matchesStatus && matchesEmptyField;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Edit3 className="w-5 h-5" />
          Batch Field Updater
        </CardTitle>
        <CardDescription>
          Select multiple records and update a field for all of them at once, or upload CSV for bulk persona rationales
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Manual Selection</TabsTrigger>
            <TabsTrigger value="csv">CSV Upload (Persona Rationale)</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4 mt-4">
            <Alert>
              <AlertDescription>
                <strong>How to use:</strong> 1) Select entity type 2) Load records 3) Select records 4) Choose field and value 5) Apply update
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Entity</Label>
                <Select value={selectedEntity} onValueChange={(v) => { setSelectedEntity(v); setRecords([]); setSelectedRecords(new Set()); setStatusFilter('all'); setSelectedField(''); setFieldValue(''); setEmptyFieldFilter(false); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITIES.map(entity => (
                      <SelectItem key={entity.value} value={entity.value}>
                        {entity.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Field to Update</Label>
                <Select value={selectedField} onValueChange={setSelectedField}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select field" />
                  </SelectTrigger>
                  <SelectContent>
                    {fieldOptions.map(field => (
                      <SelectItem key={field.key} value={field.key}>
                        {field.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedField && (
              <div className="space-y-2">
                <Label>New Value</Label>
                {selectedFieldConfig?.type === 'select' ? (
                  <Select value={fieldValue} onValueChange={setFieldValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedFieldConfig.options.map(opt => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : selectedFieldConfig?.type === 'boolean' ? (
                  <Select value={fieldValue} onValueChange={setFieldValue}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">True</SelectItem>
                      <SelectItem value="false">False</SelectItem>
                    </SelectContent>
                  </Select>
                ) : selectedFieldConfig?.type === 'date' ? (
                  <Input
                    type="date"
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    placeholder="Select date"
                  />
                ) : selectedFieldConfig?.type === 'textarea' ? (
                  <textarea
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    placeholder="Enter new value"
                    className="w-full min-h-[80px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00C2C2]"
                  />
                ) : (
                  <Input
                    value={fieldValue}
                    onChange={(e) => setFieldValue(e.target.value)}
                    placeholder={`Enter new value (e.g., ${selectedFieldConfig?.label === 'LMS Product' ? 'Canvas, Blackboard' : selectedFieldConfig?.label === 'SIS Product' ? 'Workday, Ellucian' : 'product name'})`}
                  />
                )}
              </div>
            )}

            <Button onClick={loadRecords} disabled={isLoading} className="w-full">
              {isLoading ? 'Loading...' : 'Load Records'}
            </Button>

            {records.length > 0 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Search Records</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>

                  {selectedEntity === 'Procurement' ? (
                    <div className="space-y-2">
                      <Label>Filter by Status</Label>
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Statuses</SelectItem>
                          <SelectItem value="empty">Empty/No Status</SelectItem>
                          <SelectItem value="Live">Live</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                          <SelectItem value="Awarded">Awarded</SelectItem>
                          <SelectItem value="Cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label className="opacity-0 pointer-events-none">Placeholder</Label> {/* Visual alignment */}
                      <div className="flex items-center gap-2 h-10">
                        <Checkbox
                          checked={emptyFieldFilter}
                          onCheckedChange={setEmptyFieldFilter}
                          disabled={!selectedField}
                          id="empty-field-filter"
                        />
                        <Label htmlFor="empty-field-filter" className="text-sm cursor-pointer">
                          Only show records with empty {selectedFieldConfig?.label || 'field'}
                        </Label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="border rounded-lg p-4 max-h-96 overflow-y-auto space-y-2">
                  <div className="flex items-center justify-between mb-3 pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedRecords.size === filteredRecords.length && filteredRecords.length > 0}
                        onCheckedChange={toggleAll}
                      />
                      <span className="font-semibold text-sm">
                        Select All ({selectedRecords.size} / {filteredRecords.length} selected)
                      </span>
                    </div>
                  </div>

                  {filteredRecords.slice(0, 100).map(record => (
                    <div
                      key={record.id}
                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded"
                    >
                      <Checkbox
                        checked={selectedRecords.has(record.id)}
                        onCheckedChange={() => toggleRecord(record.id)}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {selectedEntity === 'PersonFinal' 
                            ? `${record.first_name} ${record.last_name}`
                            : selectedEntity === 'AccountFinal'
                            ? record.account_name
                            : record.title}
                        </div>
                        {selectedEntity === 'Procurement' && (
                          <div className="text-xs text-gray-500">
                            {record.institution} • Status: {record.status || 'Empty'} • Deadline: {record.deadline || 'N/A'}
                          </div>
                        )}
                        {selectedField && (
                          <div className="text-xs text-gray-500">
                            Current {selectedFieldConfig?.label}: {String(record[selectedField] || 'Not set')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {filteredRecords.length > 100 && (
                    <p className="text-xs text-gray-500 text-center pt-2">
                      Showing first 100 records. Use search to narrow results.
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleBatchUpdate}
                  disabled={isUpdating || selectedRecords.size === 0 || !selectedField}
                  className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
                >
                  {isUpdating ? 'Updating...' : `Update ${selectedRecords.size} Selected Records`}
                </Button>
              </>
            )}
          </TabsContent>

          <TabsContent value="csv" className="space-y-4 mt-4">
            <Alert>
              <AlertDescription>
                <strong>CSV Format:</strong> Use columns <code className="bg-gray-100 px-1 rounded">person_id</code> and <code className="bg-gray-100 px-1 rounded">persona_score_rationale</code>. 
                The rationale can be up to 500 characters long. Each person_id must match an existing person in the database.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>Download Template</Label>
              <Button onClick={downloadCsvTemplate} variant="outline" className="w-full">
                <Download className="w-4 h-4 mr-2" />
                Download CSV Template
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-file">Upload CSV</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleCsvFileChange}
                disabled={isUploadingCsv}
              />
              {csvFile && (
                <p className="text-sm text-gray-600">
                  Selected: {csvFile.name}
                </p>
              )}
            </div>

            <Button
              onClick={handleCsvUpload}
              disabled={!csvFile || isUploadingCsv}
              className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
            >
              {isUploadingCsv ? (
                'Uploading...'
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload and Update Rationales
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
